import PropType from "prop-types";
import { useCallback, useEffect, useState } from "react";

import classNames from "classnames";
import TabContent from "./Content/TabContent";
import styles from "./TabMenu.module.scss";
import TabTitle from "./TabTitle";

const TabMenu = ({ categories, request, sortContent, customCssClass, extraTab }) => {
  const [activeTab, setActiveTab] = useState(extraTab?.id);
  const [content, setContent] = useState();
  const [groupedContent, setGroupedContent] = useState();

  const fetchLinks = useCallback(
    async category => {
      const response = await request(category);

      if (response?.data?.page?.length) {
        if (response.data.page[0].hyperlinks?.length) {
          setContent(curr => {
            if (!curr) return response.data.page;

            const found = curr.find(pageContent => pageContent.category?.value === category);
            if (found) {
              const index = curr.indexOf(found);
              if (index !== -1) {
                curr.splice(index, 1, response.data.page[0]);
                return sortContent([...curr]);
              }
            }

            return sortContent([...curr, response.data.page[0]]);
          });
        }
      }
    },
    [request, sortContent],
  );

  useEffect(() => {
    categories?.forEach(fetchLinks);
  }, [categories, request, fetchLinks]);

  const onChange = (e, value) => {
    e.preventDefault();
    if (activeTab !== value) {
      setActiveTab(value);
    }
  };

  useEffect(() => {
    if (content?.length) {
      setGroupedContent(
        content.reduce((accumulator, pageContent) => {
          const pageContents = accumulator[pageContent.category?.value] ?? [];
          pageContents.push(pageContent);

          return { ...accumulator, [pageContent.category?.value]: pageContents };
        }, {}),
      );
    }
  }, [content]);

  useEffect(() => {
    if (activeTab !== undefined) return;
    if (content?.length) setActiveTab(content[0].category?.value);
  }, [content, activeTab]);

  if (!extraTab && !content?.length) return null;

  const isExtraTabActive = extraTab && activeTab === extraTab.id;
  const tabId = value => `tab-${value}`;
  const panelId = value => `panel-${value}`;

  const onTablistKeyDown = e => {
    const tabValues = [];
    if (extraTab?.id) tabValues.push(extraTab.id);
    if (content) content.forEach(c => c.category?.value && tabValues.push(c.category.value));
    if (tabValues.length === 0) return;

    const currentIndex = tabValues.indexOf(activeTab);
    if (currentIndex === -1) return;

    let nextIndex;
    if (e.key === "ArrowRight") nextIndex = (currentIndex + 1) % tabValues.length;
    else if (e.key === "ArrowLeft") nextIndex = (currentIndex - 1 + tabValues.length) % tabValues.length;
    else if (e.key === "Home") nextIndex = 0;
    else if (e.key === "End") nextIndex = tabValues.length - 1;
    else return;

    e.preventDefault();
    const nextValue = tabValues[nextIndex];
    setActiveTab(nextValue);
    document.getElementById(tabId(nextValue))?.focus();
  };

  return (
    <div className={classNames(styles.tabContainer, customCssClass)}>
      <div className={styles.tabTitles} role="tablist" onKeyDown={onTablistKeyDown}>
        {extraTab && (
          <TabTitle
            key={extraTab.id}
            id={tabId(extraTab.id)}
            panelId={panelId(extraTab.id)}
            displayTitle={extraTab.label}
            category={{ value: extraTab.id }}
            onChange={onChange}
            active={isExtraTabActive}
          />
        )}
        {content?.map(content => (
          <TabTitle
            key={content.id}
            id={tabId(content.category?.value)}
            panelId={panelId(content.category?.value)}
            displayTitle={content.hyperlinkTitle}
            category={content.category}
            onChange={onChange}
            active={activeTab === content.category?.value}
          />
        ))}
      </div>
      {extraTab && (
        <div role="tabpanel" id={panelId(extraTab.id)} aria-labelledby={tabId(extraTab.id)} hidden={!isExtraTabActive}>
          {extraTab.content}
        </div>
      )}
      {content?.map(tab => {
        const value = tab.category?.value;
        return (
          <div
            key={tab.id}
            role="tabpanel"
            id={panelId(value)}
            aria-labelledby={tabId(value)}
            hidden={isExtraTabActive || activeTab !== value}
          >
            {groupedContent?.[value]?.map(grouped => (
              <TabContent key={grouped.id} links={grouped.hyperlinks} type={grouped.category?.value} />
            ))}
          </div>
        );
      })}
    </div>
  );
};

TabMenu.propTypes = {
  request: PropType.func,
  sortContent: PropType.func,
  categories: PropType.array,
  customCssClass: PropType.string,
  extraTab: PropType.shape({
    id: PropType.string.isRequired,
    label: PropType.string.isRequired,
    content: PropType.node,
  }),
};
export default TabMenu;
